import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Search, MapPin, Phone, Mail, ExternalLink, Star, Users, Briefcase, Calendar, BookOpen, User, CreditCard, LogOut, Filter, QrCode, CheckCircle, Store, Clock } from "lucide-react";
import AuthModal from "./components/AuthModal";
import ChatBot from "./components/ChatBot";
import PaymentModal from "./components/PaymentModal";
import BusinessApplication from "./components/BusinessApplication";
import RetailerOnboardingFlow from "./components/RetailerOnboardingFlow";
import EnhancedSearch from "./components/EnhancedSearch";
import BusinessCard from "./components/SimpleBusinessCard";
import { TermsOfSale, TermsOfUse, PrivacyPolicy, GDPRCompliance, RiskDisclosure } from "./components/LegalPages";
import PricingPage from "./components/PricingPage";
import AdminDashboard from "./components/AdminDashboard";
import AdminLoginModal from "./components/AdminLoginModal";
import LocationFirstSearch from "./components/LocationFirstSearch";
import BlogPage from "./components/BlogPage";
import InstagramFeed from "./components/InstagramFeed";
import HowItWorksPage from "./components/HowItWorksPage";
import FAQPage from "./components/FAQPage";
import ContactPage from "./components/ContactPage";
import BusinessPage from "./components/BusinessPage";
import JobPage from "./components/JobPage";
import EventPage from "./components/EventPage";
import RetailerLogin from "./components/RetailerLogin";
import RetailerDashboard from "./components/RetailerDashboard";
import { useStructuredData, useMultipleStructuredData } from "./hooks/useStructuredData";
import { 
  generateLocalBusinessSchema, 
  generateBusinessDirectorySchema,
  generateJobListingSchema,
  generateJobPostingSchema,
  generateEventSchema,
  generateEventListingSchema,
  generateBlogPostingSchema
} from "./utils/structuredData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth checker component for Google OAuth
const AuthChecker = ({ onAuthSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const processGoogleAuth = async () => {
      // Check for session_id in URL fragment (Google OAuth return)
      const fragment = window.location.hash.substring(1);
      const params = new URLSearchParams(fragment);
      const sessionId = params.get('session_id');

      if (sessionId) {
        try {
          const response = await axios.post(`${API}/auth/google-session`, {
            session_id: sessionId
          });

          // Set session cookie and notify parent
          document.cookie = `session_token=${response.data.session_token}; path=/; secure; samesite=none; max-age=${7 * 24 * 60 * 60}`;
          onAuthSuccess(response.data.user);

          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
          navigate('/');
        } catch (error) {
          console.error('Google auth processing failed:', error);
        }
      }
    };

    processGoogleAuth();
  }, [location, navigate, onAuthSuccess]);

  return null;
};

// Header Component
const Header = ({ user, onAuthClick, onPaymentClick, onBusinessApplication, onLogout }) => {
  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_be48effd-3c36-477f-be09-01d2f597ad9e/artifacts/3m4p66qd_shoppersdiscountlogo%20large-edit-20231028192141.jpg" 
              alt="Shoppers Discount Club" 
              className="h-12 w-12 rounded-full"
            />
            <div>
              <h1 className="text-xl font-bold text-charcoal">Shoppers Discount Club</h1>
              <p className="text-sm text-gray-600">UK's Premier Business Directory</p>
            </div>
          </div>
          
          <nav className="hidden md:flex items-center space-x-6">
            <a href="#directory" className="text-charcoal hover:text-primary font-medium transition-colors">Directory</a>
            <a href="#jobs" className="text-charcoal hover:text-primary font-medium transition-colors">Jobs</a>
            <a href="#events" className="text-charcoal hover:text-primary font-medium transition-colors">What's On</a>
            <a href="/blog" className="text-charcoal hover:text-primary font-medium transition-colors">Blog</a>
            <a href="/pricing" className="text-charcoal hover:text-primary font-medium transition-colors">Pricing</a>
            
            {user ? (
              <div className="flex items-center space-x-3">
                <Button 
                  onClick={onBusinessApplication}
                  size="sm" 
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  Request Listing
                </Button>
                <div className="flex items-center space-x-2 px-3 py-1 bg-gray-100 rounded-lg">
                  <User className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">{user.name}</span>
                </div>
                <Button 
                  onClick={onLogout}
                  variant="ghost" 
                  size="sm"
                  className="text-gray-600 hover:text-gray-800"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Button 
                  onClick={onBusinessApplication}
                  size="sm" 
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  Request to be Listed
                </Button>
                <Button 
                  onClick={onAuthClick}
                  variant="outline" 
                  size="sm" 
                  className="border-primary text-primary hover:bg-primary hover:text-white"
                >
                  Login
                </Button>
              </div>
            )}
          </nav>
          
          {/* Mobile menu button */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="md:hidden"
            onClick={onBusinessApplication}
          >
            <Users className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </header>
  );
};

// Hero Section Component
const HeroSection = ({ visitorCount }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const handleSearchResults = (results) => {
    setSearchResults(results);
    setShowResults(true);
  };

  const handleSearchLoading = (loading) => {
    setIsSearching(loading);
  };

  return (
    <section className="relative bg-gradient-to-br from-cream via-white to-cream py-8 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-orange/5"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl lg:text-5xl font-bold text-charcoal leading-tight mb-4">
            Real-Time
            <span className="text-primary block">Live Discounts</span>
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
            Get instant discounts when you walk past or enter participating businesses. 
            Scan QR codes for live offers across UK's best retailers and service providers.
          </p>
        </div>

        {/* Live Counter */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-orange/20 mb-8 max-w-md mx-auto">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-3 h-3 bg-primary rounded-full animate-pulse"></div>
            <span className="text-amber font-semibold text-center">
              This week: {visitorCount || 534} shoppers saved using live QR discounts
            </span>
          </div>
        </div>

        {/* New Location-First Search Component */}
        <LocationFirstSearch 
          onResults={handleSearchResults}
          onLoading={handleSearchLoading}
        />
      </div>
    </section>
  );
};

// Quick Links Section
const QuickLinks = () => {
  const links = [
    { icon: Users, title: "Business Directory", desc: "Find local businesses & offers", color: "bg-primary", href: "#directory" },
    { icon: Briefcase, title: "Job Board", desc: "Discover retail opportunities", color: "bg-orange", href: "#jobs" },
    { icon: Calendar, title: "What's On", desc: "Events, fairs & workshops", color: "bg-amber", href: "#events" },
    { icon: BookOpen, title: "Blog & Tips", desc: "Shopping guides & insights", color: "bg-primary", href: "#blog" }
  ];

  const handleLinkClick = (href) => {
    // Smooth scroll to section
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-charcoal mb-4">Quick Access</h2>
          <p className="text-gray-600">Explore everything Shoppers Discount Club has to offer</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {links.map((link, index) => (
            <Card 
              key={index} 
              className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-gray-100 hover:border-orange/30"
              onClick={() => handleLinkClick(link.href)}
            >
              <CardContent className="p-6 text-center space-y-4">
                <div className={`${link.color} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform`}>
                  <link.icon className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-charcoal group-hover:text-primary transition-colors">{link.title}</h3>
                  <p className="text-gray-600 text-sm mt-1">{link.desc}</p>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <ExternalLink className="h-4 w-4 text-primary mx-auto" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Featured Businesses Component
const FeaturedBusinesses = ({ businesses, onBusinessApplication }) => {
  // Mock enhanced business data for display
  const enhancedBusinesses = [
    {
      id: 1,
      name: 'Tesco Express Central',
      logo: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=100&h=100&fit=crop',
      address: '123 High Street, Birmingham B1 2AA',
      chatbotUrl: 'tesco-central.shoppersdiscount.club',
      isVerified: true,
      tier: 'platinum'
    },
    {
      id: 2,
      name: 'Marks & Spencer',
      logo: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=100&h=100&fit=crop',
      address: '456 Corporation Street, Birmingham B4 6QE',
      chatbotUrl: 'marks-spencer.shoppersdiscount.club',
      isVerified: true,
      tier: 'elite'
    },
    {
      id: 3,
      name: 'Sainsbury\'s Local',
      logo: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=100&h=100&fit=crop',
      address: '789 New Street, Birmingham B2 4QQ',
      chatbotUrl: 'sainsburys-local.shoppersdiscount.club',
      isVerified: true,
      tier: 'deluxe'
    },
    {
      id: 4,
      name: 'ASDA Superstore',
      logo: 'https://images.unsplash.com/photo-1578662015928-3c7b44b2c3b5?w=100&h=100&fit=crop',
      address: '321 Bull Ring, Birmingham B5 4BP',
      chatbotUrl: 'asda-superstore.shoppersdiscount.club',
      isVerified: true,
      tier: 'standard'
    },
    {
      id: 5,
      name: 'Boots Pharmacy',
      logo: 'https://images.unsplash.com/photo-1576671081837-49000212a370?w=100&h=100&fit=crop',
      address: '654 Victoria Square, Birmingham B1 1BD',
      chatbotUrl: 'boots-pharmacy.shoppersdiscount.club',
      isVerified: true,
      tier: 'deluxe'
    },
    {
      id: 6,
      name: 'Costa Coffee',
      logo: 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=100&h=100&fit=crop',
      address: '987 Grand Central, Birmingham B2 4BF',
      chatbotUrl: 'costa-coffee.shoppersdiscount.club',
      isVerified: true,
      tier: 'standard'
    },
    {
      id: 7,
      name: 'Greggs Bakery',
      logo: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=100&h=100&fit=crop',
      address: '147 Colmore Row, Birmingham B3 3AG',
      chatbotUrl: 'greggs-bakery.shoppersdiscount.club',
      isVerified: true,
      tier: 'standard'
    },
    {
      id: 8,
      name: 'Waterstones Bookshop',
      logo: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=100&h=100&fit=crop',
      address: '258 Pavilions Shopping, Birmingham B4 7SL',
      chatbotUrl: 'waterstones.shoppersdiscount.club',
      isVerified: true,
      tier: 'deluxe'
    },
    {
      id: 9,
      name: 'Next Clothing',
      logo: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=100&h=100&fit=crop',
      address: '369 Bullring Shopping, Birmingham B5 4BU',
      chatbotUrl: 'next-clothing.shoppersdiscount.club',
      isVerified: true,
      tier: 'platinum'
    },
    {
      id: 10,
      name: 'Pizza Express',
      logo: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=100&h=100&fit=crop',
      address: '741 Mailbox, Birmingham B1 1RD',
      chatbotUrl: 'pizza-express.shoppersdiscount.club',
      isVerified: true,
      tier: 'standard'
    }
  ];

  return (
    <section id="directory" className="py-16 bg-gradient-to-br from-cream/30 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-charcoal mb-4">
            Featured Businesses with Live QR Discounts
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            🎯 <strong>Must visit retailer's shop to claim discount!</strong> Click their unique link to view deals and chatbot.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {enhancedBusinesses.map((business) => (
            <Card key={business.id} className="hover:shadow-xl transition-all duration-300 cursor-pointer group">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <img 
                      src={business.logo} 
                      alt={business.name}
                      className="w-16 h-16 rounded-xl object-cover border-2 border-gray-100 group-hover:border-primary transition-colors"
                    />
                    {business.isVerified && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-charcoal group-hover:text-primary transition-colors truncate">
                        {business.name}
                      </h3>
                      <Badge 
                        variant={business.tier === 'elite' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {business.tier}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3 flex items-start gap-1">
                      <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{business.address}</span>
                    </p>
                    
                    <Button 
                      className="w-full text-sm"
                      onClick={() => window.open(`https://${business.chatbotUrl}`, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Deals & Chat
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <Card className="max-w-2xl mx-auto bg-amber-50 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-center gap-3 mb-3">
                <QrCode className="h-8 w-8 text-amber-600" />
                <h3 className="text-lg font-bold text-amber-800">QR Code Discounts</h3>
              </div>
              <p className="text-amber-700 text-sm">
                🏪 <strong>Important:</strong> You must physically enter the retailer's shop to scan QR codes and claim your live discount. 
                Online browsing shows available offers only.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-8">
          <Button 
            size="lg" 
            variant="outline" 
            className="border-primary text-primary hover:bg-primary hover:text-white"
          >
            <Filter className="h-5 w-5 mr-2" />
            Explore All Local Businesses
          </Button>
        </div>
      </div>
    </section>
  );
};

// Jobs Section Component
const JobsSection = () => {
  const mockJobs = [
    {
      id: 1,
      title: "Retail Assistant - Tesco",
      company: "Tesco Express",
      location: "Birmingham, UK", 
      type: "Part-time",
      salary: "£9.50/hour",
      posted: "2 days ago",
      application_url: "https://careers.tesco.com/job/retail-assistant-birmingham",
      expires_at: "2025-02-15"
    },
    {
      id: 2,
      title: "Store Manager - M&S",
      company: "Marks & Spencer",
      location: "Manchester, UK",
      type: "Full-time", 
      salary: "£35,000/year",
      posted: "1 week ago",
      application_url: "https://careers.marksandspencer.com/job/store-manager-manchester",
      expires_at: "2025-02-20"
    },
    {
      id: 3,
      title: "Customer Service - Sainsbury's",
      company: "Sainsbury's Local",
      location: "London, UK",
      type: "Part-time",
      salary: "£10.20/hour", 
      posted: "3 days ago",
      application_url: "https://sainsburys.jobs/job/customer-service-london",
      expires_at: "2025-02-12"
    },
    {
      id: 4,
      title: "Sales Associate - Next",
      company: "Next Clothing",
      location: "Leeds, UK",
      type: "Full-time",
      salary: "£22,000/year",
      posted: "5 days ago",
      application_url: "https://careers.next.co.uk/job/sales-associate-leeds",
      expires_at: "2025-02-25"
    }
  ];
  // Generate structured data for job listings
  const jobSchemas = React.useMemo(() => {
    return mockJobs.map(job => generateJobPostingSchema(job));
  }, []);

  // Apply job listing structured data
  useMultipleStructuredData(jobSchemas, 'jobs-schema');

  return (
    <section id="jobs" className="py-16 bg-gradient-to-br from-blue-50/30 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-charcoal mb-4">
            Latest Job Opportunities
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Discover retail, hospitality, and service opportunities with our partner businesses
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {mockJobs.map((job) => (
            <Card key={job.id} className="group hover:shadow-xl transition-all duration-300 cursor-pointer">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-charcoal group-hover:text-primary transition-colors">
                      {job.title}
                    </h3>
                    <p className="text-gray-600 font-medium">{job.company}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                      <MapPin className="h-3 w-3" />
                      {job.location}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-green-100 text-green-800 mb-2">{job.salary}</Badge>
                    <p className="text-xs text-gray-500">{job.posted}</p>
                    <p className="text-xs text-red-500 mt-1">Expires: {new Date(job.expires_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-xs">{job.type}</Badge>
                  <Button 
                    size="sm" 
                    className="group-hover:bg-primary group-hover:text-white"
                    onClick={() => window.open(job.application_url, '_blank')}
                  >
                    Apply Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-8">
          <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
            <Briefcase className="h-4 w-4 mr-2" />
            View All Jobs
          </Button>
        </div>
      </div>
    </section>
  );
};

// Events Section Component  
const EventsSection = () => {
  const mockEvents = [
    {
      id: 1,
      title: "Birmingham Farmers Market",
      location: "Bullring Square, Birmingham",
      date: "2025-01-28",
      end_date: "2025-01-28",
      time: "9:00 AM - 4:00 PM",
      type: "Market",
      description: "Fresh produce, local crafts, and artisanal foods",
      website: "https://www.eventbrite.co.uk/e/birmingham-farmers-market-tickets"
    },
    {
      id: 2,
      title: "Winter Sale Weekend", 
      location: "Grand Central Shopping, Birmingham",
      date: "2025-01-30", 
      end_date: "2025-02-01",
      time: "10:00 AM - 8:00 PM",
      type: "Sale Event",
      description: "Up to 70% off across participating retailers",
      website: "https://grandcentralbirmingham.com/events/winter-sale-weekend"
    },
    {
      id: 3,
      title: "Manchester Food Festival",
      location: "Albert Square, Manchester", 
      date: "2025-02-05",
      end_date: "2025-02-07",
      time: "11:00 AM - 6:00 PM", 
      type: "Festival",
      description: "Taste the best of Manchester's food scene",
      website: "https://www.manchesterfoodfestival.com"
    },
    {
      id: 4,
      title: "London Craft Fair",
      location: "Covent Garden, London",
      date: "2025-02-10",
      end_date: "2025-02-12",
      time: "10:00 AM - 5:00 PM",
      type: "Craft Fair", 
      description: "Handmade goods and unique gifts",
      website: "https://www.eventbrite.co.uk/e/london-craft-fair-covent-garden-tickets"
    }
  ];

  // Generate structured data for events
  const eventSchemas = React.useMemo(() => {
    return mockEvents.map(event => generateEventSchema(event));
  }, []);

  // Apply event listing structured data
  useMultipleStructuredData(eventSchemas, 'events-schema');

  return (
    <section id="events" className="py-16 bg-gradient-to-br from-amber-50/30 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-charcoal mb-4">
            What's On Near You
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Discover local markets, fairs, workshops, and community events
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {mockEvents.map((event) => (
            <Card key={event.id} className="group hover:shadow-xl transition-all duration-300 cursor-pointer">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-lg text-charcoal group-hover:text-primary transition-colors">
                        {event.title}
                      </h3>
                      <Badge className="bg-amber-100 text-amber-800 text-xs">{event.type}</Badge>
                    </div>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-1">
                      <MapPin className="h-3 w-3" />
                      {event.location}
                    </p>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-1">
                      <Clock className="h-3 w-3" />
                      {new Date(event.date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-500 mb-2">{event.time}</p>
                    <p className="text-gray-600 text-sm">{event.description}</p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="group-hover:bg-primary group-hover:text-white"
                    onClick={() => window.open(event.website, '_blank')}
                  >
                    Learn More
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-8">
          <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
            <Calendar className="h-4 w-4 mr-2" />
            View All Events
          </Button>
        </div>
      </div>
    </section>
  );
};

// Blog Section Component
const BlogSection = ({ blogPosts }) => {
  return (
    <section id="blog" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-charcoal mb-4">
            Latest Shopping Insights
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Tips, guides, and insights to help you save more and shop smarter
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {blogPosts.slice(0, 4).map((post, index) => (
            <Card 
              key={post.id || index} 
              className="group hover:shadow-xl transition-all duration-300 cursor-pointer"
              onClick={() => window.location.href = '/blog'}
            >
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-orange/10 rounded-t-lg"></div>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-bold text-base group-hover:text-primary transition-colors line-clamp-2">
                  {post.title || `Shopping Tip #${index + 1}`}
                </h3>
                <p className="text-gray-600 text-sm line-clamp-3">
                  {post.excerpt || "Discover amazing ways to save money and find the best deals across UK retailers..."}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{post.author || "SDC Team"}</span>
                  <span>{new Date(post.created_at || Date.now()).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-8">
          <Button 
            variant="outline" 
            className="border-primary text-primary hover:bg-primary hover:text-white"
            onClick={() => window.location.href = '/blog'}
          >
            Read More Articles
          </Button>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = ({ onBusinessApplication }) => {
  return (
    <footer className="bg-charcoal text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_be48effd-3c36-477f-be09-01d2f597ad9e/artifacts/3m4p66qd_shoppersdiscountlogo%20large-edit-20231028192141.jpg" 
                alt="Shoppers Discount Club" 
                className="h-10 w-10 rounded-full"
              />
              <div>
                <h3 className="font-bold">Shoppers Discount Club</h3>
                <p className="text-sm text-gray-400">Live instant discounts</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm">
              The UK's premier platform for real-time QR code discounts connecting shoppers with amazing deals, job opportunities, and local events.
            </p>
            <div className="flex flex-wrap gap-3">
              <a 
                href="https://www.facebook.com/shoppersdiscountclub" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-400 transition-colors"
                title="Facebook"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/shoppersdiscountclub" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-pink-400 transition-colors"
                title="Instagram"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987c6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.348-1.051-2.348-2.348s1.051-2.348 2.348-2.348s2.348 1.051 2.348 2.348S9.746 16.988 8.449 16.988zM12.017 7.129c1.35 0 2.44 1.090 2.44 2.44s-1.090 2.44-2.44 2.44s-2.44-1.090-2.44-2.44S10.667 7.129 12.017 7.129zM15.585 16.988c-1.297 0-2.348-1.051-2.348-2.348s1.051-2.348 2.348-2.348s2.348 1.051 2.348 2.348S16.882 16.988 15.585 16.988z"/>
                </svg>
              </a>
              <a 
                href="https://www.x.com/shoppersdisclub" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-200 transition-colors"
                title="X (Twitter)"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a 
                href="https://youtube.com/@shoppersdiscountclub" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-red-400 transition-colors"
                title="YouTube"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a 
                href="https://business-apply.shoppers.discount.club" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-600 transition-colors"
                title="LinkedIn"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a 
                href="mailto:order@shoppersdiscount.club" 
                className="text-gray-400 hover:text-primary transition-colors"
                title="Email"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </a>
              <a 
                href="https://shoppersdiscount.club" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary transition-colors text-sm"
              >
                Original Website
              </a>
              <a 
                href="https://get-more-customers.carrd.co/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-orange transition-colors text-sm"
              >
                Get More Customers
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Directory</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="#supermarkets" className="hover:text-primary transition-colors">🛒 Supermarkets</a></li>
              <li><a href="#food-drink" className="hover:text-primary transition-colors">🍴 Food & Drink</a></li>
              <li><a href="#fashion" className="hover:text-primary transition-colors">👗 Fashion & Beauty</a></li>
              <li><a href="#home" className="hover:text-primary transition-colors">🏠 Home & Lifestyle</a></li>
              <li><a href="#health" className="hover:text-primary transition-colors">⚽ Health & Fitness</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Resources</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="#jobs" className="hover:text-primary transition-colors">Job Board</a></li>
              <li><a href="#events" className="hover:text-primary transition-colors">What's On Events</a></li>
              <li><a href="#blog" className="hover:text-primary transition-colors">Blog & Tips</a></li>
              <li><a href="/admin" className="hover:text-primary transition-colors">Admin Portal</a></li>
              <li><a href="https://www.instagram.com/shoppersdiscountclub?igsh=MWZvczJxenFuem15OA==" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Instagram News</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Business</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="#" onClick={(e) => { e.preventDefault(); onBusinessApplication && onBusinessApplication(); }} className="hover:text-primary transition-colors cursor-pointer">Request to be Listed</a></li>
              <li><a href="/pricing" className="hover:text-primary transition-colors">Pricing Packages</a></li>
              <li><a href="/how-it-works" className="hover:text-primary transition-colors">How It Works</a></li>
              <li><a href="/contact" className="hover:text-primary transition-colors">Contact Support</a></li>
              <li><a href="/faq" className="hover:text-primary transition-colors">FAQ</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Legal & Compliance</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="/terms-of-sale" className="hover:text-primary transition-colors">Terms of Sale</a></li>
              <li><a href="/terms-of-use" className="hover:text-primary transition-colors">Terms of Use</a></li>
              <li><a href="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</a></li>
              <li><a href="/gdpr-compliance" className="hover:text-primary transition-colors">GDPR Compliance</a></li>
              <li><a href="/risk-disclosure" className="hover:text-primary transition-colors">Risk Disclosure</a></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-400 text-center md:text-left">
              <p>&copy; 2026 Shoppers Discount Club. All rights reserved.</p>
              <p className="mt-1">
                Copyright Registration: <a 
                  href="https://www.copyrightregistrationservice.com/cgi-bin/verify.cgi?crsnum=28844029047" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 underline"
                >
                  28844029047
                </a>
              </p>
            </div>
            <div className="text-sm text-gray-400 text-center md:text-right">
              <p>Real-time QR code discounts for UK businesses</p>
              <p className="mt-1">Powered by AI technology • Supporting local communities</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
const App = () => {
  const [businesses, setBusinesses] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [visitorCount, setVisitorCount] = useState(532);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showBusinessApplication, setShowBusinessApplication] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Handle retailer onboarding with package pre-selection
  const handleRetailerOnboarding = (packageId) => {
    setSelectedPackage(packageId);
    setShowBusinessApplication(true);
  };

  // Generate structured data for homepage
  const homepageSchemas = React.useMemo(() => {
    const schemas = [];
    
    // Add business directory schema if businesses exist
    if (businesses.length > 0) {
      schemas.push(generateBusinessDirectorySchema(businesses));
      
      // Add individual business schemas for featured businesses
      businesses.forEach(business => {
        schemas.push(generateLocalBusinessSchema(business));
      });
    }
    
    // Add blog posting schemas for featured blog posts
    if (blogPosts.length > 0) {
      blogPosts.slice(0, 4).forEach(post => {
        schemas.push(generateBlogPostingSchema(post));
      });
    }
    
    return schemas;
  }, [businesses, blogPosts]);

  // Apply structured data to homepage
  useMultipleStructuredData(homepageSchemas, 'homepage-schema');

  const checkExistingAuth = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const adminUser = localStorage.getItem('admin_user');
      
      // Check if admin user is stored
      if (adminUser) {
        try {
          const parsedAdminUser = JSON.parse(adminUser);
          if (parsedAdminUser && parsedAdminUser.role === 'admin') {
            setUser(parsedAdminUser);
            return;
          }
        } catch (e) {
          localStorage.removeItem('admin_user');
        }
      }
      
      // Check regular user token
      if (token) {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      }
    } catch (error) {
      // Clear invalid tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('admin_user');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Check existing auth first
      await checkExistingAuth();

      // Fetch businesses
      try {
        let businessResponse = await axios.get(`${API}/businesses?featured_only=true&limit=6`);
        
        if (businessResponse.data.length === 0) {
          businessResponse = await axios.get(`${API}/businesses?limit=6`);
        }
        
        setBusinesses(businessResponse.data);
      } catch (error) {
        console.error('Error fetching businesses:', error);
        setBusinesses([
          {
            id: '1',
            name: 'Tesco Express',
            description: 'Your local convenience store with fresh groceries and essentials.',
            category: 'supermarket',
            address: 'High Street, London',
            phone: '020 1234 5678',
            is_featured: true,
            current_offers: ['10% off weekly shop over £50']
          },
          {
            id: '2', 
            name: 'Marks & Spencer',
            description: 'Premium food, clothing and home products.',
            category: 'retail',
            address: 'Oxford Street, London',
            is_featured: true,
            current_offers: ['Buy 2 Get 1 Free on selected items']
          }
        ]);
      }

      // Fetch blog posts
      try {
        const blogResponse = await axios.get(`${API}/blog/recent?limit=4`);
        setBlogPosts(blogResponse.data);
        console.log('Blog posts fetched successfully:', blogResponse.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setBlogPosts([
          {
            id: '1',
            title: 'Unlock the Power of Live Discounts - A Case Study',
            excerpt: 'Explore how AI conversational chatbots and live QR discounts are revolutionizing retail engagement, driving customers into physical stores.',
            author: 'SDC Team',
            created_at: '2025-09-27T05:58:38.650261Z'
          }
        ]);
      }

      // Fetch visitor count
      try {
        const countResponse = await axios.get(`${API}/visitor-count`);
        setVisitorCount(countResponse.data.count);
      } catch (error) {
        console.error('Error fetching visitor count:', error);
        setVisitorCount(532); // Default value
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setShowAuthModal(false);
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('admin_user'); // Clear admin user data
      document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      setUser(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-charcoal font-medium">Loading Shoppers Discount Club...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AuthChecker onAuthSuccess={handleAuthSuccess} />
        <Routes>
          <Route path="/" element={
            <>
              <Header 
                user={user}
                onAuthClick={() => setShowAuthModal(true)}
                onPaymentClick={() => setShowPaymentModal(true)}
                onBusinessApplication={() => setShowBusinessApplication(true)}
                onLogout={handleLogout}
              />
              <HeroSection visitorCount={visitorCount} />
              <QuickLinks />
              <FeaturedBusinesses businesses={businesses} onBusinessApplication={() => setShowBusinessApplication(true)} />
              <JobsSection />
              <EventsSection />
              <BlogSection blogPosts={blogPosts} />
              <InstagramFeed />
              <Footer onBusinessApplication={() => setShowBusinessApplication(true)} />
              
              {/* Modals */}
              <AuthModal 
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                onAuthSuccess={handleAuthSuccess}
              />
              
              <PaymentModal 
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                user={user}
              />
              
              <RetailerOnboardingFlow 
                isOpen={showBusinessApplication}
                onClose={() => {
                  setShowBusinessApplication(false);
                  setSelectedPackage(null);
                }}
                selectedPackage={selectedPackage}
                user={user}
              />
              
              {/* ChatBot */}
              <ChatBot user={user} />
            </>
          } />
          <Route path="/dashboard" element={
            <>
              <Header 
                user={user}
                onAuthClick={() => setShowAuthModal(true)}
                onPaymentClick={() => setShowPaymentModal(true)}
                onBusinessApplication={() => setShowBusinessApplication(true)}
                onLogout={handleLogout}
              />
              <div className="min-h-screen bg-gray-50 py-12">
                <div className="container mx-auto px-4">
                  <div className="text-center">
                    <h1 className="text-3xl font-bold text-charcoal mb-4">
                      Welcome to Your Dashboard
                    </h1>
                    {user ? (
                      <p className="text-gray-600">Hello {user.name}! Manage your business listings and account here.</p>
                    ) : (
                      <p className="text-gray-600">Please log in to access your dashboard.</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          } />
          <Route path="/payment-success" element={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <Card className="max-w-md mx-auto text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Star className="h-8 w-8 text-white" />
                  </div>
                  <h1 className="text-2xl font-bold text-charcoal mb-2">Payment Successful!</h1>
                  <p className="text-gray-600 mb-6">Thank you for upgrading your account. Your new features are now active.</p>
                  <Button onClick={() => window.location.href = '/'} className="bg-primary hover:bg-primary/90">
                    Return to Homepage
                  </Button>
                </CardContent>
              </Card>
            </div>
          } />
          <Route path="/payment-cancel" element={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <Card className="max-w-md mx-auto text-center">
                <CardContent className="p-8">
                  <h1 className="text-2xl font-bold text-charcoal mb-2">Payment Cancelled</h1>
                  <p className="text-gray-600 mb-6">No charges were made. You can try again anytime.</p>
                  <Button onClick={() => window.location.href = '/'} variant="outline">
                    Return to Homepage
                  </Button>
                </CardContent>
              </Card>
            </div>
          } />
          <Route path="/terms-of-sale" element={<TermsOfSale />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/gdpr-compliance" element={<GDPRCompliance />} />
          <Route path="/risk-disclosure" element={<RiskDisclosure />} />
          <Route path="/pricing" element={
            <>
              <Header 
                user={user}
                onAuthClick={() => setShowAuthModal(true)}
                onPaymentClick={() => setShowPaymentModal(true)}
                onBusinessApplication={() => setShowBusinessApplication(true)}
                onLogout={handleLogout}
              />
              <PricingPage onBusinessApplication={handleRetailerOnboarding} />
              <Footer onBusinessApplication={() => setShowBusinessApplication(true)} />
              
              {/* Modals */}
              <AuthModal 
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                onAuthSuccess={handleAuthSuccess}
              />
              
              <PaymentModal 
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                user={user}
              />
              
              <RetailerOnboardingFlow 
                isOpen={showBusinessApplication}
                onClose={() => {
                  setShowBusinessApplication(false);
                  setSelectedPackage(null);
                }}
                selectedPackage={selectedPackage}
                user={user}
              />
              
              {/* ChatBot */}
              <ChatBot user={user} />
            </>
          } />
          <Route path="/blog" element={<BlogPage onBackToHome={() => window.location.href = '/'} />} />
          <Route path="/how-it-works" element={<HowItWorksPage onBackToHome={() => window.location.href = '/'} />} />
          <Route path="/faq" element={<FAQPage onBackToHome={() => window.location.href = '/'} />} />
          <Route path="/contact" element={<ContactPage onBackToHome={() => window.location.href = '/'} />} />
          <Route path="/business/:businessId" element={<BusinessPage onBackToHome={() => window.location.href = '/'} />} />
          <Route path="/jobs/:jobId" element={<JobPage onBackToHome={() => window.location.href = '/'} />} />
          <Route path="/events/:eventId" element={<EventPage onBackToHome={() => window.location.href = '/'} />} />
          
          {/* Retailer Portal Routes */}
          <Route path="/retailer/login" element={<RetailerLogin onLoginSuccess={handleAuthSuccess} />} />
          <Route path="/retailer" element={<RetailerLogin onLoginSuccess={handleAuthSuccess} />} />
          <Route path="/retailer/dashboard" element={<RetailerDashboard />} />
          
          {/* Admin Portal Route */}
          <Route path="/admin" element={
            user?.role === 'admin' ? (
              <AdminDashboard user={user} />
            ) : (
              <AdminLoginModal onLoginSuccess={handleAuthSuccess} />
            )
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
};

export default App;